import CurrencyCode from "@/module/main/dto/CurrencyCodeEnum";
import IAmount from "@/module/main/dto/IAmount";
import Amount from "@/module/main/dto/Amount";
import BaseMapper from "@/module/main/service/BaseMapper";
import IEndpoints from "@/module/main/model/applicationForm/IEndpoints";
import IOriginationContext from "@/module/main/dto/IOriginationContextRequest";
import IMerchantURLs from "@/module/main/dto/IMerchantURLs";
import IChannelPhoneSettings from "@/module/main/dto/IChannelPhoneSettings";
import OriginationChannelType from "@/module/main/dto/OriginationChannelTypeEnum";
import MerchantURLs from "@/module/main/dto/MerchantURLs";
import ChannelPhoneSettings from "@/module/main/dto/ChannelPhoneSettings";
import CallRequest from "@/module/main/dto/CallRequest";
import HcDate from "@/module/main/dto/HcDate";
import moment from "moment/moment";
import OriginationContext from "@/module/main/dto/OriginationContextRequest";
import OriginationChannel from "@/module/main/dto/OriginationChannel";
import IModel from "@/module/main/model/applicationForm/IApplicationForm";
import IFinancedSubjectBase from "@/module/main/dto/IFinancedSubjectBase";
import FinancedSubjectBase from "@/module/main/dto/FinancedSubjectBase";
import FinancedSubjectTypeEnum from "@/module/main/dto/FinancedSubjectTypeEnum";
import ICashBase from "@/module/main/dto/ICashBase";
import ISettingsLoanWallet from "@/module/main/dto/ISettingsLoanWallet";
import SettingsLoanWallet from "@/module/main/dto/SettingsLoanWallet";
import ICashFinancing from "@/module/main/dto/ICashFinancing";
import CashFinancing from "@/module/main/dto/CashFinancing";
import CashBase from "@/module/main/dto/CashBase";
import ILiabilityBase from "@/module/main/dto/ILiabilityBase";
import ISettingsConsolidation from "@/module/main/dto/ISettingsConsolidation";
import SettingsConsolidation from "@/module/main/dto/SettingsConsolidation";
import ILiabilityFinancing from "@/module/main/dto/ILiabilityFinancing";
import LiabilityFinancing from "@/module/main/dto/LiabilityFinancing";
import LiabilityBase from "@/module/main/dto/LiabilityBase";
import CashFinancingType from "@/module/main/dto/CashFinancingTypeEnum";
import SettingsCashLoan from "@/module/main/dto/financing-v3/SettingsCashLoan";
import ISettingsCashLoan from "@/module/main/dto/financing-v3/ISettingsCashLoan";

abstract class BaseMapperV2 extends BaseMapper {
    protected createAmount(price: string, currencyCode: CurrencyCode): IAmount | undefined
    {
        let amount: IAmount | undefined = undefined;
        const priceNumber: number | undefined = this.stringToNumber(price);

        if (priceNumber != undefined)
        {
            amount = new Amount(Math.round(priceNumber * 100), currencyCode);
        }

        return amount;
    }

    protected getOriginationContext(endpoints: IEndpoints): IOriginationContext
    {
        let online: IMerchantURLs | undefined;
        let phone: IChannelPhoneSettings | undefined;
        let type: OriginationChannelType | undefined;

        if (endpoints.isReturnUrl == "true") {
            type = OriginationChannelType.ONLINE;
            online = new MerchantURLs(
                endpoints.approvedRedirect || undefined,
                endpoints.notificationEndpoint || undefined,
                endpoints.rejectedRedirect || undefined
            );
        } else {
            type = OriginationChannelType.PHONE;
            phone = new ChannelPhoneSettings(
                new CallRequest([new HcDate(moment(endpoints.callMeDatetime).format())]),
                endpoints.callMeEndpoint
            );
        }

        return new OriginationContext(
            new OriginationChannel(type, online, phone),
            endpoints.sourceId || undefined
        );
    }

    protected getFinancedSubjectBase(application: IModel): IFinancedSubjectBase
    {
        return new FinancedSubjectBase(
            application.loan.financedSubjectType,
            application.loan.financedSubjectType == FinancedSubjectTypeEnum.CASH ? this.getCashBase(application) : undefined,
            application.loan.financedSubjectType == FinancedSubjectTypeEnum.LIABILITY ? this.getLiabilityBase(application) : undefined,
        );
    }

    protected getCashBase(application: IModel): ICashBase
    {
        let cashLoan: ISettingsCashLoan | undefined;
        let loanWallet: ISettingsLoanWallet | undefined;

        const cashFinancingType = application.loan.cashFinancingType || CashFinancingType.LOAN_WALLET;
        const preferredInstallment = !!application.loan.preferredInstallment
            ? this.createAmount(application.loan.preferredInstallment, application.currency.code)
            : undefined;
        const productCode = application.loan.productCode || undefined;

        if (cashFinancingType === CashFinancingType.CASH_LOAN) {
            cashLoan = new SettingsCashLoan(
                preferredInstallment,
                productCode
            );
        } else {
            loanWallet = new SettingsLoanWallet(
                preferredInstallment,
                productCode
            );
        }

        const financing = new CashFinancing(cashFinancingType, loanWallet, cashLoan);

        return new CashBase(
            !!application.loan.totalPrice
                ? this.createAmount(application.loan.totalPrice, application.currency.code)
                : undefined,
            financing
        );
    }

    protected getLiabilityBase(application: IModel): ILiabilityBase {
        const consolidation: ISettingsConsolidation = new SettingsConsolidation(
            !!application.loan.preferredInstallment
                ? this.createAmount(application.loan.preferredInstallment, application.currency.code)
                : undefined,
            application.loan.productCode || undefined
        );

        const financing: ILiabilityFinancing = new LiabilityFinancing(consolidation);

        return new LiabilityBase(
            !!application.loan.totalPrice
                ? this.createAmount(application.loan.totalPrice, application.currency.code)
                : undefined,
            financing
        );
    }
}

export default BaseMapperV2;
